import type { ISubaccount, Security } from '@talos/kyoko';
import {
  Button,
  EMPTY_ARRAY,
  FormControlSizes,
  FormGroup,
  Grid,
  isMultileg,
  MixpanelEvent,
  MixpanelEventProperty,
  OrderPresetIcon,
  Stack,
  SymbolSelector,
  SymbolSelectorAppearance,
  Toggle,
  UnifiedLiquidityTooltip,
  useMarketAccountsContext,
  useMarketsContext,
  useMixpanel,
  useSecurity,
  useUnifiedLiquidity,
  useUnifiedLiquidityParameter,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useSubAccounts } from '../../../../../providers';
import { MULTILEG_MARKET_ACCOUNT } from '../../../../../providers/OMSContext.types';
import { useOrderPresets, type IOrderPreset } from '../../../../../providers/OrderPresetsContext';
import { useTradingSettings } from '../../../../../providers/TradingSettingsContext';
import type { SingleSymbolPanelConfig } from '../../../../../types/LayoutConfig';
import { usePanelContext } from '../../../../Layout/providers/PanelContextProvider';
import { useMarketSelectorBuyingPowerEnrichment } from '../../../../MarketSelector/enrichments/useMarketSelectorBuyingPowerEnrichment';
import { useUnsettledEnrichment } from '../../../../MarketSelector/enrichments/useUnsettledEnrichment';
import { useMarketDataMarketSelectorItems } from '../../../../MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import { MarketSelector } from '../../../../MarketSelector/MarketSelector';
import { useMarketSelectorAggregationPresets } from '../../../../MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorValue } from '../../../../MarketSelector/types';
import { useMarketSelector } from '../../../../MarketSelector/useMarketSelector';
import { useCreateMultilegDialog } from '../../../../MultilegCombo/CreateMultilegDialog';
import { MultiLegSummary } from '../../../../MultiLegSummary';
import {
  OrderPresetSearchSelect,
  useOrderPresetSearchSelectOptions,
} from '../../../../OrderPresets/OrderPresetSearchSelect';
import { SubAccountSearchSelect } from '../../../../SubAccounts/SubAccountSearchSelect';
import { useSubAccountSearchSelectOptions } from '../../../../SubAccounts/useSubAccountSearchSelectOptions';
import { getSelectableMarketItemNames } from './getSelectableMarketItemNames';

interface SingleSymbolPanelSettingsProps {
  availableSecurities: Security[];
  symbol: string | undefined;
  aggregation: string | undefined;
  markets: string[] | undefined;
  marketAccounts: string[] | undefined;
  onSymbolChange: (symbol: string | undefined) => void;
  onUpdateParameters: (config: Partial<SingleSymbolPanelConfig>) => void;
  onClose: () => void;
  allowMultileg: boolean;
  orderPresetID: string | undefined;
  subAccount?: string;
  enableUnifiedLiquidity: boolean | undefined;
  allowUnifiedLiquidity?: boolean;

  customSettings?: React.ReactNode;
  showPresets?: boolean;
  shoeMarketAccounts?: boolean;
}

export function SingleSymbolPanelSettings({
  symbol,
  markets,
  marketAccounts,
  onSymbolChange,
  onUpdateParameters,
  aggregation,
  availableSecurities,
  enableUnifiedLiquidity,
  allowUnifiedLiquidity,
  allowMultileg,
  orderPresetID,
  subAccount,
  customSettings,
  onClose,
  showPresets = true,
  shoeMarketAccounts = true,
}: SingleSymbolPanelSettingsProps) {
  const mixpanel = useMixpanel();
  const { isPopout } = usePanelContext();

  const security = useSecurity(symbol);
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { enableDefaultMarketCardSubAccount } = useTradingSettings();
  const { subAccountsByName } = useSubAccounts();
  const { updateOrderPreset, orderPresetsByID, orderPresetsList } = useOrderPresets();

  const subAccountOptions = useSubAccountSearchSelectOptions();
  const orderPreset = orderPresetID ? orderPresetsByID.get(orderPresetID) : undefined;
  const subAccountSelection = subAccount ? subAccountsByName.get(subAccount) : undefined;
  const unifiedLiquidityParameter = useUnifiedLiquidityParameter();
  const unifiedLiquidityToken = useUnifiedLiquidity(symbol);
  const showUnifiedLiquidityOption = !!unifiedLiquidityToken;
  const orderPresetOptions = useOrderPresetSearchSelectOptions({ security });

  const filteredSecurities = useMemo(() => {
    return availableSecurities.filter(security => allowMultileg || !isMultileg(security));
  }, [availableSecurities, allowMultileg]);

  const handleNewMarketSelectorChange = useCallback(
    (value: MarketSelectorValue) => {
      const selections = value.selections;
      const markets = selections.filter(mktOrAccName => marketsByName.has(mktOrAccName));
      const marketAccounts = selections.filter(mktOrAccName => marketAccountsByName.has(mktOrAccName));
      const aggregation = value.type === 'preset' ? value.presetID : undefined;
      onUpdateParameters({
        markets,
        marketAccounts,
        aggregation,
      });
    },
    [onUpdateParameters, marketsByName, marketAccountsByName]
  );

  const marketSelectorItems = useMarketDataMarketSelectorItems({
    symbol,
    includeUnifiedLiquidityMarkets: enableUnifiedLiquidity,
  });

  const unsettledEnrichment = useUnsettledEnrichment({ security, marketSelectorItems });

  const buyingPowerEnrichment = useMarketSelectorBuyingPowerEnrichment({
    symbol: security?.Symbol ?? '',
    currency: security?.BaseCurrency ?? '',
    threshold: '10s',
    quantity: undefined,
    tag: 'PanelSettingsMarketSelector',
    side: undefined,
    simpleModeVisibleColIDs: EMPTY_ARRAY,
  });

  const enrichmentSpecs = useMemo(
    () => compact([unsettledEnrichment, buyingPowerEnrichment]),
    [unsettledEnrichment, buyingPowerEnrichment]
  );

  const { aggregationPresets, aggregationPresetsByID } = useMarketSelectorAggregationPresets();
  const itemsMap = useMemo(
    () => new Map(marketSelectorItems.map(option => [option.name, option])),
    [marketSelectorItems]
  );
  const marketSelectorValue: MarketSelectorValue = useMemo(() => {
    const selections = [...(markets ?? []), ...(marketAccounts ?? [])];
    if (aggregation) {
      return { type: 'preset', presetID: aggregation, selections };
    } else if (markets?.length || marketAccounts?.length) {
      return { type: 'custom', selections };
    } else if (orderPreset?.aggregation) {
      const presetID = orderPreset.aggregation;
      const selections = getSelectableMarketItemNames(presetID, aggregationPresetsByID, itemsMap);
      return {
        type: 'preset',
        presetID,
        selections,
      };
    }
    return { type: 'custom', selections };
  }, [itemsMap, marketAccounts, markets, aggregation, orderPreset?.aggregation, aggregationPresetsByID]);

  const marketSelector = useMarketSelector({
    value: marketSelectorValue,
    onChange: handleNewMarketSelectorChange,
    presets: aggregationPresets,
    items: marketSelectorItems,
    enrichmentSpecs,
  });

  const unifiedLiquidityMarkets = useMemo(
    () => (marketAccounts ? marketAccounts.filter(ma => ma !== MULTILEG_MARKET_ACCOUNT) : EMPTY_ARRAY),
    [marketAccounts]
  );

  const editMultilegDialog = useCreateMultilegDialog({
    onUseSymbol: onSymbolChange,
  });

  const handleChangeUnifiedLiquidity = useCallback(
    (checked: boolean) => {
      mixpanel.track(MixpanelEvent.ToggleUnifiedLiquidity, {
        [MixpanelEventProperty.Enabled]: checked,
      });
      onUpdateParameters({ enableUnifiedLiquidity: checked });
    },
    [onUpdateParameters, mixpanel]
  );

  const handleOpenEditMultileg = useCallback(
    (symbol: string) => {
      if (!security) {
        return;
      }
      mixpanel.track(MixpanelEvent.EditMultileg);
      editMultilegDialog.onEdit(symbol);
    },
    [security, mixpanel, editMultilegDialog]
  );

  const handleUnifiedLiquidityMarketsChange = useCallback(
    (marketAccounts: string[]) => {
      onUpdateParameters({ marketAccounts: [...marketAccounts, MULTILEG_MARKET_ACCOUNT] });
    },
    [onUpdateParameters]
  );

  const handleOrderPresetToggleFavorite = useCallback(
    (orderPreset: IOrderPreset) => {
      const updated = {
        ...orderPreset,
        isFavorite: !orderPreset.isFavorite,
      };
      mixpanel.track(MixpanelEvent.FavoriteOrderPreset);
      updateOrderPreset(orderPreset.id, updated);
    },
    [updateOrderPreset, mixpanel]
  );

  const handleOrderPresetChange = useCallback(
    (newOrderPreset: IOrderPreset | undefined) => {
      const orderPresetID = newOrderPreset?.id;
      const updatedParams: Partial<SingleSymbolPanelConfig> = {
        orderPresetID,
      };
      const newAggregation = newOrderPreset?.aggregation;
      if (newAggregation) {
        updatedParams.aggregation = newAggregation;
        updatedParams.marketAccounts = getSelectableMarketItemNames(newAggregation, aggregationPresetsByID, itemsMap);
      } else if (newOrderPreset == null && orderPreset != null && orderPreset.aggregation === aggregation) {
        updatedParams.aggregation = undefined;
        updatedParams.marketAccounts = [];
      }
      const allocations = newOrderPreset?.allocations;
      if (enableDefaultMarketCardSubAccount && allocations && allocations.length === 1 && allocations[0]?.subAccount) {
        updatedParams.subAccount = allocations[0]?.subAccount;
      } else if (
        enableDefaultMarketCardSubAccount &&
        newOrderPreset == null &&
        orderPreset != null &&
        orderPreset.allocations.length === 1 &&
        subAccount === orderPreset.allocations[0].subAccount
      ) {
        updatedParams.subAccount = undefined;
      }
      mixpanel.track(MixpanelEvent.SelectOrderPreset);
      onUpdateParameters(updatedParams);
    },
    [
      onUpdateParameters,
      aggregationPresetsByID,
      itemsMap,
      orderPreset,
      aggregation,
      subAccount,
      mixpanel,
      enableDefaultMarketCardSubAccount,
    ]
  );

  const handleSubAccountChange = useCallback(
    (subAccount?: ISubaccount) => {
      mixpanel.track(MixpanelEvent.SelectSubAccount);
      onUpdateParameters({ subAccount: subAccount?.Name });
    },
    [onUpdateParameters, mixpanel]
  );

  return (
    <>
      <Stack p="spacing12" overflow="auto" scrollbarGutter="stable" pr="spacing4">
        <FormGroup label="Symbol">
          <SymbolSelector
            availableSecurities={filteredSecurities}
            appearance={SymbolSelectorAppearance.Basic}
            size={FormControlSizes.Small}
            showGroups={true}
            symbol={symbol}
            placeholder="Select a symbol"
            onSymbolChanged={onSymbolChange}
            dropdownWidth="480px"
            portalize={true}
            dropdownPlacement="bottom-end"
            showFavorites
          />
        </FormGroup>
        {showPresets && orderPresetsList.length > 0 ? (
          <FormGroup controlId="order-preset" label="Order Preset">
            <OrderPresetSearchSelect
              onChange={handleOrderPresetChange}
              data-testid="panel-settings-order-preset"
              prefix={<OrderPresetIcon />}
              selection={orderPreset}
              options={orderPresetOptions}
              size={FormControlSizes.Small}
              placeholder="Optional"
              id="order-form-preset-selector"
              onToggleFavorite={handleOrderPresetToggleFavorite}
            />
          </FormGroup>
        ) : null}
        {shoeMarketAccounts && enableDefaultMarketCardSubAccount && (
          <FormGroup label="SubAccount" size={FormControlSizes.Small}>
            <SubAccountSearchSelect
              selection={subAccountSelection}
              size={FormControlSizes.Small}
              options={subAccountOptions}
              onChange={handleSubAccountChange}
              data-testid="sub-account-selector"
              placeholder="Optional"
            />
          </FormGroup>
        )}
        {security && allowUnifiedLiquidity && showUnifiedLiquidityOption && unifiedLiquidityParameter && (
          <FormGroup
            size={FormControlSizes.Small}
            label={unifiedLiquidityParameter.DisplayName}
            tooltip={
              <UnifiedLiquidityTooltip
                security={security}
                marketAccounts={marketAccounts ?? EMPTY_ARRAY}
                markets={markets ?? EMPTY_ARRAY}
                unifiedLiquidityParameter={unifiedLiquidityParameter}
                showDescription={true}
              />
            }
            inline
            mb="spacing8"
            controlId="title"
            labelWidth="6rem"
            alignItems="center"
            justifyContent="space-between"
          >
            <Toggle
              size={FormControlSizes.Default}
              checked={enableUnifiedLiquidity ?? false}
              onChange={handleChangeUnifiedLiquidity}
              data-testid="panel-settings-unified-liquidity-toggle"
            />
          </FormGroup>
        )}
        {customSettings}
        <FormGroup label="Markets" display="flex" flexDirection="column" mb={0}>
          {isMultileg(security) ? (
            <MultiLegSummary
              security={security}
              readOnly={false}
              supportsAllMarkets={true}
              touched={true}
              unifiedLiquidityToken={enableUnifiedLiquidity ? unifiedLiquidityToken : undefined}
              unifiedLiquidityMarkets={unifiedLiquidityMarkets}
              onEditSymbol={handleOpenEditMultileg}
              onUnifiedLiquidityMarketsChange={handleUnifiedLiquidityMarketsChange}
              allocations={EMPTY_ARRAY}
            />
          ) : (
            <MarketSelector
              size={FormControlSizes.Small}
              marketSelectorAPI={marketSelector}
              showCredentialsOnlyToggle
              panelOffset={8}
              panelPlacement="auto"
              isNarrow={isPopout}
              placeholder={orderPreset?.aggregation ? orderPreset.aggregation : 'Select Aggregation...'}
            />
          )}
        </FormGroup>
      </Stack>
      <Grid
        mt="auto"
        gap="spacing8"
        p="spacing12"
        gridTemplateColumns="1fr"
        borderColor="backgroundDivider"
        borderTop="solid 1px"
      >
        <Button onClick={onClose} size={FormControlSizes.Small}>
          Close
        </Button>
      </Grid>
    </>
  );
}
